"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useMapLocation = void 0;
var _auth = require("@ori/auth");
var _react = require("react");
var _useDeliveryChannelAddressQuery = require("../api/useDeliveryChannelAddressQuery");
var _checkoutDeliveryContext = require("../contexts/checkoutDeliveryContext");
var _logger = require("../logger");
var _models = require("../models");
var _utils = require("../utils");
var _useCurrentLocation = require("./useCurrentLocation");
var _usePickupPointsSearchConfiguration = require("./usePickupPointsSearchConfiguration");
var _usePrimaryAddressLocation = require("./usePrimaryAddressLocation");
/*
Startup location type priority (when found, skip the other location types):

- search text filled-in -> 1st instant result available -> find location based on its deliveryChannelId
- current location status - even if search text item location was found, this is still needed for the current location button
- primary address location - logged-in user only
- fallback to per-market predefined location

The above should apply only on map initialization, otherwise only re-search instant results when switching tabs, if any search text is set.
Solves also updates for new instant search results or clicking a pickup point icon in the already opened map view.
*/

const hookName = 'useMapLocation';
const useMapLocation = ({
  disableLatitudeOffset,
  instantResultChannelId,
  isActive,
  selectedDeliveryChannelCoordinates
}) => {
  const {
    customerId = _auth.NOT_LOGGED_IN_USER_ID
  } = (0, _checkoutDeliveryContext.useCheckoutDeliveryContext)();
  const {
    mapMethods,
    mapConfiguration: {
      zoomAfterSelection
    } = {}
  } = (0, _usePickupPointsSearchConfiguration.usePickupPointsSearchConfiguration)();
  // Flag indicating that startup location setup during map initialization was done already.
  const [isInitialLocationDone, setIsInitialLocationDone] = (0, _react.useState)(false);
  // Stored location value for the case when map wasn't initialized yet when trying to set
  // location. In this case, the stored value will be used in onMapInitialization handler.
  const storedLocation = (0, _react.useRef)(undefined);
  const storedLocationType = (0, _react.useRef)(undefined);

  /**
   * We might want variable settings for zoom for each of the possible location types/ selections.
   * So far, only the searched map result location has a speacial behavior. Map zoom is being
   * preserved in other cases.
   */
  const getZoomForLocationType = (0, _react.useCallback)((mapLocationType, currentZoom) => mapLocationType === _models.MapLocationType.Searched ? zoomAfterSelection : currentZoom, [zoomAfterSelection]);

  /**
   * Sets a new map center position if it differs significantly from current map position.
   * Optionally sets also zoom if special zoom requirements are associated with given
   * map positioning type (e.g. when locating searched position).
   *
   * When a pickup point is selected, i.e. for instant search result selection or manually
   * clicked pickup point in map, the center position is adjusted so that the pickup point
   * appears in the top quarter of the map viewport.
   */
  const locatePositionInMap = (0, _react.useCallback)((location, mapLocationType) => {
    if (!location) {
      return;
    }
    const {
      latitude,
      longitude
    } = location;
    const isOnMapInitCallback = mapLocationType === _models.MapLocationType.WhenInitialized;
    const usedLocationType = isOnMapInitCallback ? storedLocationType.current : mapLocationType;
    const isSelectedPickupPoint = [_models.MapLocationType.PickupPoint, _models.MapLocationType.Searched].includes(usedLocationType);
    setIsInitialLocationDone(true);
    if (mapMethods.current) {
      const {
        location: currentMapLocation,
        zoom
      } = mapMethods.current.getViewSettings();
      const zoomToApply = getZoomForLocationType(usedLocationType, zoom);
      const isEqualZoom = zoom === zoomToApply;
      const description = `${usedLocationType}${isOnMapInitCallback ? ' (stored location)' : ''}`;
      // To be able to adjust the center for selected pickup point properly, we need to have the final zoom first.
      if (!isEqualZoom) {
        (0, _logger.logDebug)(`${hookName}: locatePositionInMap() - ${description}: setting zoom to ${zoomToApply} ...`);
        mapMethods.current.setLocationOrZoom({
          zoom: zoomToApply
        });
      }
      /*
      The view center adjustment is now based on map view bounds. So no pixel calculation
      of map and/or displayed pickup point card height is involved. If such a precision would be needed,
      we would have to utilize those components' div refs here.
      */
      const {
        north,
        south
      } = mapMethods.current.getViewBounds();
      const latitudeOffset = isSelectedPickupPoint ? Math.abs(north - south) * 0.25 : 0;
      (0, _logger.logDebug)(`${hookName}: locatePositionInMap():`, {
        latitude,
        latitudeOffset,
        isSelectedPickupPoint
      });
      const newLocation = {
        latitude: disableLatitudeOffset ? latitude : latitude - latitudeOffset,
        longitude
      };
      if (!(0, _utils.isEqualLocation)(currentMapLocation, newLocation)) {
        (0, _logger.logDebug)(`${hookName}: locatePositionInMap() - ${description}:`, '\n\tcurrentMapLocation', currentMapLocation, '\n\tnew location', newLocation);
        // Adjust the map center location if needed.
        mapMethods.current.setLocationOrZoom({
          location: newLocation
        });
      }
      storedLocation.current = undefined;
    } else {
      storedLocation.current = location;
      storedLocationType.current = mapLocationType;
      (0, _logger.logDebug)(`${hookName}: locatePositionInMap() - ${mapLocationType}: mapMethods not initialized yet, stored location for later.`);
    }
  }, [disableLatitudeOffset, getZoomForLocationType, mapMethods]);
  const onMapInitialization = (0, _react.useCallback)(() => {
    (0, _logger.logDebug)(`${hookName}: onMapInitialization() called.`);
    locatePositionInMap(storedLocation.current, _models.MapLocationType.WhenInitialized);
  }, [locatePositionInMap]);

  /*
  Location of 1st item from instant search, if any was found.
  Intentionally running the address API here instead of coordinates to cache the address data.
  There is some probability for the same data to be used for displaying selected pickup point
  detail later.
  */
  const {
    deliveryChannelAddress,
    isDeliveryChannelAddressLoading
  } = (0, _useDeliveryChannelAddressQuery.useDeliveryChannelAddressQuery)(isActive ? instantResultChannelId : undefined);
  const {
    coordinates: foundLocation
  } = deliveryChannelAddress ?? {};

  /*
  Wait for the selected pickup point coords when the searchTerm is set.
  In instant search, a pickup point can be selected by the user, or automatically
  when switching from list tab to map tab (1st found item is then selected).
  */
  const isFinishedSearch = Boolean(instantResultChannelId) && !isDeliveryChannelAddressLoading;
  const isNoSearchResult = instantResultChannelId === undefined || isFinishedSearch && !foundLocation;
  const isSearchResult = isFinishedSearch && Boolean(foundLocation);

  /*
  Current user location hook should be executed even if the search text was specified,
  as the user might still want to click the current location button to navigate the map.
  */
  const {
    currentLocation,
    currentLocationStatus
  } = (0, _useCurrentLocation.useCurrentLocation)({
    isActive,
    onSuccess: coordinates => {
      (0, _logger.logDebug)(`${hookName}: current location onSuccess(), coords:`, coordinates);
    }
  });
  const isCurrentLocationEnabled = currentLocationStatus === _models.CurrentLocationStatus.Success;
  const isCurrentLocationLoading = currentLocationStatus === _models.CurrentLocationStatus.Waiting;
  const isCurrentLocationButtonVisible = isCurrentLocationEnabled && Boolean(currentLocation);

  /*
  Checking for primary address only when the above cases for getting initial location
  didn't succeed. This involves getting the primary addres, then executing geocoding search
  for the address text or optionally for the address' city, then navigating map to the coordinates.
  */
  const isPrimaryAddressActive = isActive && isNoSearchResult && !isInitialLocationDone && !isCurrentLocationLoading && !isCurrentLocationEnabled && customerId !== _auth.NOT_LOGGED_IN_USER_ID;
  const {
    isPrimaryAddressLoading,
    primaryAddress,
    primaryAddressLocation
  } = (0, _usePrimaryAddressLocation.usePrimaryAddressLocation)({
    isActive: isPrimaryAddressActive,
    onSuccess: ({
      foundPosition
    }) => {
      (0, _logger.logDebug)(`${hookName}: primary address location onSuccess(), coords:`, foundPosition);
    }
  });
  if (isActive) {
    // logDebug(`${hookName} called. Local props:`, {
    //   currentLocation,
    //   currentLocationStatus,
    //   foundLocation,
    //   isCurrentLocationEnabled,
    //   isCurrentLocationButtonVisible,
    //   isFinishedSearch,
    //   isInitialLocationDone,
    //   isNoSearchResult,
    //   isPrimaryAddressLoading,
    //   isSearchResult,
    //   locatePositionInMap,
    //   onMapInitialization,
    //   primaryAddress,
    //   primaryAddressLocation,
    // });
  }
  (0, _react.useEffect)(() => {
    if (!isActive) {
      return;
    }
    if (isSearchResult) {
      // When switching tabs, always try to update map position if searched text was found.
      locatePositionInMap(foundLocation, _models.MapLocationType.Searched);
      return;
    }
    // Once the initial map positioning is done, skip current location or primary address location.
    // Skip also when searched text might not be loaded yet or similar cases.
    if (isInitialLocationDone || !isNoSearchResult) {
      return;
    }
    // When a pickup point is selected, the map should be centered to the pickup point.
    if (typeof selectedDeliveryChannelCoordinates !== 'undefined') {
      locatePositionInMap(selectedDeliveryChannelCoordinates, _models.MapLocationType.Searched);
      return;
    }
    if (isCurrentLocationEnabled && currentLocation) {
      locatePositionInMap(currentLocation, _models.MapLocationType.Current);
      return;
    }
    if (isPrimaryAddressActive && !isPrimaryAddressLoading && primaryAddressLocation) {
      locatePositionInMap(primaryAddressLocation, _models.MapLocationType.PrimaryAddress);
    }
  }, [currentLocation, foundLocation, isActive, isCurrentLocationEnabled, isInitialLocationDone, isNoSearchResult, isPrimaryAddressActive, isPrimaryAddressLoading, isSearchResult, locatePositionInMap, primaryAddressLocation, selectedDeliveryChannelCoordinates]);
  return {
    currentLocation,
    currentLocationStatus,
    isCurrentLocationEnabled,
    isCurrentLocationButtonVisible,
    isInitialLocationDone,
    isPrimaryAddressLoading,
    locatePositionInMap,
    onMapInitialization,
    primaryAddress,
    primaryAddressLocation
  };
};
exports.useMapLocation = useMapLocation;